import React, { useState, useRef, useCallback } from 'react';
import * as XLSX from 'xlsx';
import {
    X, Upload, ChevronRight, ChevronLeft, Check, AlertCircle,
    FileSpreadsheet, RefreshCw, Tag, Wand2, Info
} from 'lucide-react';

const API = process.env.REACT_APP_API_URL;

// Try to auto-match an Excel column to a field name
const autoMatch = (fieldName, columns) => {
    const normalize = s => s.toLowerCase().replace(/[^a-z0-9]/g, '');
    const fn = normalize(fieldName);
    // exact
    let match = columns.find(c => normalize(c) === fn);
    if (match) return match;
    // contains
    match = columns.find(c => normalize(c).includes(fn) || fn.includes(normalize(c)));
    return match || null;
};

// Common alias mappings
const ALIAS_MAP = {
    name: ['name', 'fullname', 'full_name', 'attendeename'],
    email: ['email', 'emailaddress', 'email_address', 'mail'],
    phone: ['phone', 'mobile', 'mobileno', 'mobile_no', 'phoneno', 'contact', 'number'],
    firstName: ['firstname', 'first_name', 'fname'],
    lastName: ['lastname', 'last_name', 'lname'],
    organisation: ['company', 'companyname', 'company_name', 'organization', 'organisation', 'org'],
    designation: ['designation', 'title', 'jobtitle', 'job_title', 'role'],
};

const smartAutoMatch = (fieldName, fieldId, columns) => {
    const normalize = s => s.toLowerCase().replace(/[^a-z0-9]/g, '');
    const fn = normalize(fieldName);
    const fid = normalize(fieldId || '');

    // Check alias map
    for (const [key, aliases] of Object.entries(ALIAS_MAP)) {
        if (aliases.includes(fn) || aliases.includes(fid)) {
            const col = columns.find(c => aliases.includes(normalize(c)));
            if (col) return col;
        }
    }
    return autoMatch(fieldName, columns);
};

// ── Step indicator ─────────────────────────────────────────────────────────────
const Steps = ({ current }) => {
    const steps = ['Upload File', 'Map Columns', 'Preview', 'Import'];
    return (
        <div className="flex items-center gap-0 mb-6">
            {steps.map((s, i) => (
                <React.Fragment key={i}>
                    <div className="flex items-center gap-2">
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-colors ${
                            i < current ? 'bg-indigo-600 border-indigo-600 text-white'
                            : i === current ? 'border-indigo-600 text-indigo-600 bg-white'
                            : 'border-gray-200 text-gray-400 bg-white'
                        }`}>
                            {i < current ? <Check className="w-3.5 h-3.5" /> : i + 1}
                        </div>
                        <span className={`text-xs font-semibold hidden sm:block ${i === current ? 'text-indigo-600' : i < current ? 'text-gray-600' : 'text-gray-300'}`}>{s}</span>
                    </div>
                    {i < steps.length - 1 && <div className={`flex-1 h-0.5 mx-2 ${i < current ? 'bg-indigo-600' : 'bg-gray-200'}`} />}
                </React.Fragment>
            ))}
        </div>
    );
};

// ── Column select dropdown ────────────────────────────────────────────────────
const ColSelect = ({ value, onChange, columns, placeholder = 'Skip' }) => (
    <select
        value={value || ''}
        onChange={e => onChange(e.target.value || null)}
        className="w-full px-2.5 py-1.5 border border-gray-200 rounded-lg text-xs text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400"
    >
        <option value="">{placeholder}</option>
        {columns.map(c => <option key={c} value={c}>{c}</option>)}
    </select>
);

// ── Main Modal ─────────────────────────────────────────────────────────────────
const BulkUploadModal = ({ event, onClose, onImported }) => {
    const [step, setStep] = useState(0);
    const [file, setFile] = useState(null);
    const [columns, setColumns] = useState([]);
    const [previewRows, setPreviewRows] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const [dragging, setDragging] = useState(false);

    // Mapping state: { [fieldId]: columnName | null }
    const [mapping, setMapping] = useState({});
    // Combined fields: { [fieldId]: [col1, '__literal__: ', col2] }
    const [combined, setCombined] = useState({});
    const [combineOpen, setCombineOpen] = useState({});

    const [categoryColumn, setCategoryColumn] = useState(null);
    const [registrationIdColumn, setRegistrationIdColumn] = useState(null);
    const [paymentStatusColumn, setPaymentStatusColumn] = useState(null);
    const [sendEmails, setSendEmails] = useState(false);

    const [importing, setImporting] = useState(false);
    const [result, setResult] = useState(null);

    const fileRef = useRef();

    // ── Parse file client-side ─────────────────────────────────────────────────
    const parseFile = useCallback((f) => {
        setFile(f);
        const reader = new FileReader();
        reader.onload = (e) => {
            const wb = XLSX.read(e.target.result, { type: 'array' });
            const ws = wb.Sheets[wb.SheetNames[0]];
            const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });
            if (rows.length === 0) return;
            const cols = Object.keys(rows[0]);
            setColumns(cols);
            setPreviewRows(rows.slice(0, 5));
            setTotalRows(rows.length);

            // Auto-detect mapping
            const autoMap = {};
            for (const field of (event.participantFields || [])) {
                const match = smartAutoMatch(field.fieldName, field.id, cols);
                autoMap[field.id] = match || null;
            }
            setMapping(autoMap);

            // Auto-detect category column
            const catCol = cols.find(c => {
                const n = c.toLowerCase();
                return n.includes('ticket') || n.includes('category') || n.includes('type') || n.includes('class');
            });
            setCategoryColumn(catCol || null);

            // Auto-detect payment status
            const payCol = cols.find(c => c.toLowerCase().includes('status'));
            setPaymentStatusColumn(payCol || null);

            setStep(1);
        };
        reader.readAsArrayBuffer(f);
    }, [event.participantFields]);

    const handleDrop = (e) => {
        e.preventDefault();
        setDragging(false);
        const f = e.dataTransfer.files[0];
        if (f) parseFile(f);
    };

    const handleFileInput = (e) => {
        const f = e.target.files[0];
        if (f) parseFile(f);
    };

    // ── Get effective mapping value for a field ────────────────────────────────
    const getEffective = (fieldId) => combined[fieldId] || mapping[fieldId];

    const getMappedValue = (row, fieldId) => {
        const cfg = getEffective(fieldId);
        if (!cfg) return '';
        if (Array.isArray(cfg)) {
            return cfg.map(c => c.startsWith('__literal__:') ? c.replace('__literal__:', '') : String(row[c] || '')).join('').trim();
        }
        return String(row[cfg] || '').trim();
    };

    // ── Run import ─────────────────────────────────────────────────────────────
    const runImport = async () => {
        setImporting(true);
        try {
            const formData = new FormData();
            formData.append('file', file);

            // Build final mapping object (replace combined arrays)
            const finalMapping = {};
            for (const field of (event.participantFields || [])) {
                const cfg = getEffective(field.id);
                if (cfg) finalMapping[field.id] = cfg;
            }
            formData.append('mapping', JSON.stringify(finalMapping));
            if (categoryColumn) formData.append('categoryColumn', categoryColumn);
            if (registrationIdColumn) formData.append('registrationIdColumn', registrationIdColumn);
            if (paymentStatusColumn) formData.append('paymentStatusColumn', paymentStatusColumn);
            formData.append('sendEmails', sendEmails ? 'true' : 'false');

            const res = await fetch(`${API}/api/v2/events/${event._id}/bulk-upload`, {
                method: 'POST',
                body: formData
            });
            const data = await res.json();
            setResult(data);
            setStep(3);
            if (data.success && onImported) onImported(data);
        } catch (err) {
            setResult({ success: false, message: err.message });
            setStep(3);
        } finally {
            setImporting(false);
        }
    };

    const fields = event.participantFields || [];

    // ── STEP 0: Upload ─────────────────────────────────────────────────────────
    const StepUpload = () => (
        <div>
            <div
                onDragOver={e => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={handleDrop}
                onClick={() => fileRef.current.click()}
                className={`border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-colors ${
                    dragging ? 'border-indigo-400 bg-indigo-50' : 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50'
                }`}
            >
                <FileSpreadsheet className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-sm font-semibold text-gray-700 mb-1">Drop your Excel file here</p>
                <p className="text-xs text-gray-400">or click to browse · .xlsx, .xls supported</p>
                <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleFileInput} />
            </div>
            <div className="mt-4 p-3 bg-blue-50 border border-blue-100 rounded-xl flex gap-2">
                <Info className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
                <p className="text-xs text-blue-700">
                    You'll map columns to participant fields in the next step. Categories not in the event will be auto-created.
                </p>
            </div>
        </div>
    );

    // ── STEP 1: Map Columns ────────────────────────────────────────────────────
    const StepMap = () => (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500">
                    <span className="font-semibold text-gray-700">{totalRows}</span> rows found in <span className="font-mono text-gray-600">{file?.name}</span>
                </p>
                <button
                    onClick={() => {
                        const autoMap = {};
                        for (const field of fields) {
                            const match = smartAutoMatch(field.fieldName, field.id, columns);
                            autoMap[field.id] = match || null;
                        }
                        setMapping(autoMap);
                    }}
                    className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 font-semibold"
                >
                    <Wand2 className="w-3.5 h-3.5" /> Auto-detect
                </button>
            </div>

            {/* Participant Fields Mapping */}
            <div className="border border-gray-200 rounded-xl overflow-hidden">
                <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 grid grid-cols-2 gap-4">
                    <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Event Field</span>
                    <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Excel Column</span>
                </div>
                <div className="divide-y divide-gray-100 max-h-64 overflow-y-auto">
                    {fields.map(field => (
                        <div key={field.id} className="px-4 py-2.5 grid grid-cols-2 gap-4 items-start">
                            <div>
                                <p className="text-xs font-semibold text-gray-700">{field.fieldName}</p>
                                <p className="text-[10px] text-gray-400 capitalize">{field.fieldType} · {field.validation}</p>
                            </div>
                            <div className="space-y-1.5">
                                {/* If combine mode is open */}
                                {combineOpen[field.id] ? (
                                    <div className="space-y-1.5">
                                        <div className="flex items-center gap-1.5">
                                            <ColSelect
                                                value={Array.isArray(combined[field.id]) ? combined[field.id][0] : ''}
                                                onChange={v => setCombined(p => ({ ...p, [field.id]: [v, '__literal__: ', combined[field.id]?.[2] || ''] }))}
                                                columns={columns}
                                                placeholder="First column"
                                            />
                                            <span className="text-gray-400 text-xs shrink-0">+</span>
                                            <ColSelect
                                                value={Array.isArray(combined[field.id]) ? combined[field.id][2] : ''}
                                                onChange={v => setCombined(p => ({ ...p, [field.id]: [combined[field.id]?.[0] || '', '__literal__: ', v] }))}
                                                columns={columns}
                                                placeholder="Second column"
                                            />
                                        </div>
                                        <button
                                            onClick={() => { setCombineOpen(p => ({ ...p, [field.id]: false })); setCombined(p => { const n = { ...p }; delete n[field.id]; return n; }); }}
                                            className="text-[10px] text-red-500 hover:text-red-700"
                                        >
                                            Remove combine
                                        </button>
                                    </div>
                                ) : (
                                    <div className="space-y-1">
                                        <ColSelect
                                            value={mapping[field.id]}
                                            onChange={v => setMapping(p => ({ ...p, [field.id]: v }))}
                                            columns={columns}
                                        />
                                        <button
                                            onClick={() => { setCombineOpen(p => ({ ...p, [field.id]: true })); setMapping(p => ({ ...p, [field.id]: null })); }}
                                            className="text-[10px] text-indigo-500 hover:text-indigo-700"
                                        >
                                            + Combine two columns
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Special fields */}
            <div className="border border-indigo-100 bg-indigo-50/40 rounded-xl overflow-hidden">
                <div className="px-4 py-2 border-b border-indigo-100 flex items-center gap-2">
                    <Tag className="w-3.5 h-3.5 text-indigo-500" />
                    <span className="text-xs font-bold text-indigo-700">Special Columns</span>
                </div>
                {/* Send emails toggle */}
                <div className="px-4 py-3 border-b border-indigo-100/60 flex items-center justify-between bg-amber-50/60">
                    <div>
                        <p className="text-xs font-semibold text-gray-700">Send confirmation emails</p>
                        <p className="text-[10px] text-gray-400 mt-0.5">Off by default — enables batched sending (5/sec) to avoid SMTP rate limits</p>
                    </div>
                    <button
                        type="button"
                        onClick={() => setSendEmails(v => !v)}
                        className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${sendEmails ? 'bg-indigo-600' : 'bg-gray-300'}`}
                    >
                        <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${sendEmails ? 'translate-x-5' : 'translate-x-0.5'}`} />
                    </button>
                </div>
                <div className="divide-y divide-indigo-100/60">
                    {/* Category */}
                    <div className="px-4 py-2.5 grid grid-cols-2 gap-4 items-center">
                        <div>
                            <p className="text-xs font-semibold text-gray-700">Category</p>
                            <p className="text-[10px] text-gray-400">New values auto-created</p>
                        </div>
                        <ColSelect value={categoryColumn} onChange={setCategoryColumn} columns={columns} placeholder="Skip" />
                    </div>
                    {/* Registration ID */}
                    {!event.generateRegistrationId && (
                        <div className="px-4 py-2.5 grid grid-cols-2 gap-4 items-center">
                            <div>
                                <p className="text-xs font-semibold text-gray-700">Registration ID</p>
                                <p className="text-[10px] text-gray-400">From Excel column</p>
                            </div>
                            <ColSelect value={registrationIdColumn} onChange={setRegistrationIdColumn} columns={columns} placeholder="Skip" />
                        </div>
                    )}
                    {/* Payment Status */}
                    <div className="px-4 py-2.5 grid grid-cols-2 gap-4 items-center">
                        <div>
                            <p className="text-xs font-semibold text-gray-700">Payment / Attendance Status</p>
                            <p className="text-[10px] text-gray-400">Maps to payment status</p>
                        </div>
                        <ColSelect value={paymentStatusColumn} onChange={setPaymentStatusColumn} columns={columns} placeholder="Skip (use default)" />
                    </div>
                </div>
            </div>
        </div>
    );

    // ── STEP 2: Preview ────────────────────────────────────────────────────────
    const StepPreview = () => {
        const mappedFields = fields.filter(f => getEffective(f.id));
        const catValues = categoryColumn
            ? [...new Set(previewRows.map(r => String(r[categoryColumn] || '').trim()).filter(Boolean))]
            : [];
        const existingCatNames = (event.participantCategories || []).map(c => c.name.toLowerCase());
        const newCats = catValues.filter(v => !existingCatNames.includes(v.toLowerCase()));

        return (
            <div className="space-y-4">
                {/* Summary */}
                <div className="grid grid-cols-3 gap-3">
                    <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-3 text-center">
                        <p className="text-2xl font-black text-indigo-700">{totalRows}</p>
                        <p className="text-[10px] text-indigo-500 font-semibold uppercase">Participants</p>
                    </div>
                    <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3 text-center">
                        <p className="text-2xl font-black text-emerald-700">{mappedFields.length}</p>
                        <p className="text-[10px] text-emerald-500 font-semibold uppercase">Fields Mapped</p>
                    </div>
                    <div className={`border rounded-xl p-3 text-center ${newCats.length > 0 ? 'bg-amber-50 border-amber-100' : 'bg-gray-50 border-gray-100'}`}>
                        <p className={`text-2xl font-black ${newCats.length > 0 ? 'text-amber-700' : 'text-gray-400'}`}>{newCats.length}</p>
                        <p className={`text-[10px] font-semibold uppercase ${newCats.length > 0 ? 'text-amber-500' : 'text-gray-400'}`}>New Categories</p>
                    </div>
                </div>

                {/* New categories warning */}
                {newCats.length > 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex gap-2">
                        <AlertCircle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                        <div>
                            <p className="text-xs font-semibold text-amber-800">New categories will be created:</p>
                            <div className="flex flex-wrap gap-1 mt-1">
                                {newCats.map(c => (
                                    <span key={c} className="px-2 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-bold rounded-md">{c}</span>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Preview table */}
                <div>
                    <p className="text-xs font-semibold text-gray-500 mb-2">Preview (first 5 rows)</p>
                    <div className="overflow-x-auto rounded-xl border border-gray-200">
                        <table className="w-full text-xs">
                            <thead className="bg-gray-50 border-b border-gray-200">
                                <tr>
                                    {mappedFields.map(f => (
                                        <th key={f.id} className="px-3 py-2 text-left font-semibold text-gray-500 whitespace-nowrap">{f.fieldName}</th>
                                    ))}
                                    {categoryColumn && <th className="px-3 py-2 text-left font-semibold text-indigo-500 whitespace-nowrap">Category</th>}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {previewRows.map((row, i) => (
                                    <tr key={i} className="hover:bg-gray-50">
                                        {mappedFields.map(f => (
                                            <td key={f.id} className="px-3 py-2 text-gray-700 max-w-[150px] truncate">{getMappedValue(row, f.id)}</td>
                                        ))}
                                        {categoryColumn && <td className="px-3 py-2 text-indigo-600 font-medium">{String(row[categoryColumn] || '')}</td>}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    };

    // ── STEP 3: Result ─────────────────────────────────────────────────────────
    const StepResult = () => {
        if (importing) return (
            <div className="flex flex-col items-center justify-center py-16 gap-4">
                <RefreshCw className="w-10 h-10 text-indigo-500 animate-spin" />
                <p className="text-sm font-semibold text-gray-600">Importing participants...</p>
                <p className="text-xs text-gray-400">Please wait, this may take a moment</p>
            </div>
        );

        if (!result) return null;

        return (
            <div className="space-y-4">
                <div className={`rounded-2xl p-5 text-center ${result.success ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
                    {result.success ? (
                        <>
                            <Check className="w-12 h-12 text-emerald-500 mx-auto mb-2" />
                            <p className="text-xl font-black text-emerald-700">{result.created} imported</p>
                            <p className="text-sm text-emerald-600 mt-1">of {result.total} rows processed</p>
                        </>
                    ) : (
                        <>
                            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-2" />
                            <p className="text-sm font-bold text-red-700">{result.message || 'Import failed'}</p>
                        </>
                    )}
                </div>

                {result.newCategories?.length > 0 && (
                    <div className="bg-amber-50 border border-amber-100 rounded-xl p-3">
                        <p className="text-xs font-bold text-amber-700 mb-1.5">New categories created:</p>
                        <div className="flex flex-wrap gap-1">
                            {result.newCategories.map(c => (
                                <span key={c} className="px-2 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-bold rounded-md">{c}</span>
                            ))}
                        </div>
                    </div>
                )}

                {result.errors > 0 && (
                    <div className="bg-red-50 border border-red-100 rounded-xl p-3">
                        <p className="text-xs font-bold text-red-700 mb-1">{result.errors} rows had errors:</p>
                        <div className="space-y-0.5 max-h-32 overflow-y-auto">
                            {(result.errorDetails || []).map((e, i) => (
                                <p key={i} className="text-[10px] text-red-600">Row {e.row}: {e.error}</p>
                            ))}
                        </div>
                    </div>
                )}

                <button onClick={onClose} className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-sm rounded-xl transition-colors">
                    Done
                </button>
            </div>
        );
    };

    return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                            <Upload className="w-4 h-4 text-indigo-600" />
                        </div>
                        <div>
                            <h2 className="text-sm font-bold text-gray-900">Bulk Upload Participants</h2>
                            <p className="text-[10px] text-gray-400">{event.eventName}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-6 py-5">
                    <Steps current={step} />

                    {step === 0 && <StepUpload />}
                    {step === 1 && <StepMap />}
                    {step === 2 && <StepPreview />}
                    {step === 3 && <StepResult />}
                </div>

                {/* Footer */}
                {step < 3 && (
                    <div className="px-6 py-4 border-t border-gray-100 flex justify-between">
                        <button
                            onClick={() => setStep(s => Math.max(0, s - 1))}
                            disabled={step === 0}
                            className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-gray-600 hover:text-gray-800 disabled:opacity-30 transition-colors"
                        >
                            <ChevronLeft className="w-4 h-4" /> Back
                        </button>

                        {step === 2 ? (
                            <button
                                onClick={runImport}
                                disabled={importing}
                                className="flex items-center gap-2 px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold rounded-xl disabled:opacity-50 transition-colors"
                            >
                                {importing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                                Import {totalRows} Participants
                            </button>
                        ) : (
                            <button
                                onClick={() => setStep(s => s + 1)}
                                disabled={step === 0}
                                className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold rounded-xl disabled:opacity-50 transition-colors"
                            >
                                Continue <ChevronRight className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default BulkUploadModal;
